import { useCallback, useEffect, useState } from 'react';

import { PDFDocument } from 'pdf-lib';

import { usePdfCreateFreezeMutation } from '@/app/services/freeze';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { RECOVERY_CODES_BY_COMPANY } from '@/pages/freeze/utils/constants';
import { useCurrentUser } from '@/services/auth';
import { useToast } from '@/services/toast';

import { processEmbryosForPrint } from './processEmbryosForPrint';
import { PrintPdfFunction, UseFreezePdfPrintParams } from './types';

const BATCH_SIZE = 20;

export const useFreezePdfPrint = ({
  getEmbryoData,
  onComplete,
}: UseFreezePdfPrintParams) => {
  const toast = useToast();
  const user = useCurrentUser();
  const company = user.data?.companies.find((c) => c.isActive);
  const recoveryCode = company
    ? RECOVERY_CODES_BY_COMPANY[
        company.code as keyof typeof RECOVERY_CODES_BY_COMPANY
      ]
    : undefined;

  const [printPdfFreeze] = usePdfCreateFreezeMutation();
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    if (isPrinting) {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        e.preventDefault();
        const message =
          'PDF generation is in progress. Leaving now will interrupt the process.';
        e.returnValue = message;
        return message;
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [isPrinting]);

  const printPdf: PrintPdfFunction = useCallback(
    async (filename: string) => {
      setIsPrinting(true);

      const message = toast.show({
        severity: 'info',
        summary: 'PDF Generation Started',
        detail: 'Generating PDF... Please do not navigate away from this page.',
        sticky: true,
      });

      try {
        const { selectedTempEmbryos, selectedLiveEmbryos, fertilizations } =
          getEmbryoData();

        const items = processEmbryosForPrint(
          selectedTempEmbryos,
          selectedLiveEmbryos,
          fertilizations,
          filename,
          recoveryCode,
        );

        const input = items.map(({ freezeMethod, isTemp, ...rest }) => rest);

        if (input.length <= BATCH_SIZE) {
          const { data: result } = await printPdfFreeze({
            items: input,
            filename,
          });

          if (result?.pdfCreateFreeze) {
            const pdfBytes = result?.pdfCreateFreeze;
            const blob = new Blob([new Uint8Array(pdfBytes)], {
              type: 'application/pdf',
            });
            const pdfUrl = URL.createObjectURL(blob);
            window.open(pdfUrl, '_blank');
            setTimeout(() => URL.revokeObjectURL(pdfUrl), 500);

            toast.update(message, {
              severity: 'success',
              summary: 'PDF Generated Successfully',
              detail: `${filename} has been generated and opened.`,
              sticky: false,
              life: ToastLife.MEDIUM,
            });
          }
        } else {
          const batches = [];
          for (let i = 0; i < input.length; i += BATCH_SIZE) {
            batches.push(input.slice(i, i + BATCH_SIZE));
          }

          const pdfBuffers = [];
          for (let i = 0; i < batches.length; i++) {
            const batch = batches[i];

            if (batches.length > 1) {
              toast.update(message, {
                severity: 'info',
                summary: `Processing Batch ${i + 1} of ${batches.length}`,
                detail: `Generating PDF batch with ${batch.length} embryos...`,
                sticky: false,
                life: ToastLife.MEDIUM,
              });
            }

            const { data: result } = await printPdfFreeze({
              items: batch,
              filename: filename,
            });

            if (result?.pdfCreateFreeze) {
              pdfBuffers.push(new Uint8Array(result?.pdfCreateFreeze));
            }
          }

          if (pdfBuffers.length > 0) {
            if (pdfBuffers.length === 1) {
              const blob = new Blob([pdfBuffers[0]], {
                type: 'application/pdf',
              });
              const pdfUrl = URL.createObjectURL(blob);
              window.open(pdfUrl, '_blank');
              setTimeout(() => URL.revokeObjectURL(pdfUrl), 500);
            } else {
              toast.update(message, {
                severity: 'info',
                summary: 'Merging PDFs',
                detail: `Combining ${pdfBuffers.length} PDF batches into a single document...`,
                sticky: false,
                life: ToastLife.MEDIUM,
              });

              try {
                const mergedPdf = await PDFDocument.create();

                for (const pdfBuffer of pdfBuffers) {
                  const pdf = await PDFDocument.load(pdfBuffer);
                  const copiedPages = await mergedPdf.copyPages(
                    pdf,
                    pdf.getPageIndices(),
                  );
                  copiedPages.forEach((page) => mergedPdf.addPage(page));
                }

                const mergedPdfBytes = await mergedPdf.save();
                const blob = new Blob([new Uint8Array(mergedPdfBytes)], {
                  type: 'application/pdf',
                });
                const pdfUrl = URL.createObjectURL(blob);
                window.open(pdfUrl, '_blank');
                setTimeout(() => URL.revokeObjectURL(pdfUrl), 500);

                toast.update(message, {
                  severity: 'success',
                  summary: 'PDF Generated Successfully',
                  detail: `${filename} has been generated from ${pdfBuffers.length} batches and opened.`,
                  sticky: false,
                  life: ToastLife.MEDIUM,
                });
              } catch (mergeError) {
                console.error('Error merging PDFs:', mergeError);
                pdfBuffers.forEach((pdfBuffer, index) => {
                  const blob = new Blob([pdfBuffer], {
                    type: 'application/pdf',
                  });
                  const pdfUrl = URL.createObjectURL(blob);
                  window.open(pdfUrl, '_blank');
                  setTimeout(
                    () => URL.revokeObjectURL(pdfUrl),
                    500 + index * 100,
                  );
                });

                toast.update(message, {
                  severity: 'warn',
                  summary: 'PDF Merge Failed',
                  detail: `Could not merge PDFs. Opened ${pdfBuffers.length} separate files instead.`,
                  sticky: false,
                  life: ToastLife.MEDIUM,
                });
              }
            }
          }
        }
      } catch (error) {
        toast.update(message, {
          severity: 'error',
          summary: 'Error printing',
          detail: error?.toString(),
          sticky: false,
          life: ToastLife.MEDIUM,
        });
        console.error('Error creating freeze report:', error);
      } finally {
        setIsPrinting(false);
        onComplete?.();
      }
    },
    [toast, getEmbryoData, recoveryCode, printPdfFreeze, onComplete],
  );

  return {
    printPdf,
    isPrinting,
  };
};
